/**
 * #1417 — перевод значений инвентаря (кузов, топливо, КПП, опции) на язык
 * покупателя.
 *
 * Инвентарь хранит канонические английские значения — те же списки, к которым
 * b2b-api приводит данные (`dealer-inventory/validators/field-validators.ts`,
 * `normalize-final-format.ts`). В URL фильтров и в поиске агента значение
 * остаётся каноническим (`?bodyStyle=SUV`), переводится только подпись.
 *
 * Словарь: `showcase.values.<field>.<slug>` в `src/i18n/{lang}.json`.
 * Namespace `showcase` живёт только в b2b-demo (в b2b-ui его нет) — ключи
 * правятся здесь, а не в b2b-ui.
 *
 * Fallback: lang → en → старый `translateValue` (только fuelType/transmission —
 * у Autodis они во французском, «Essence»/«Automatique») → значение как есть.
 * Опции без ключа (рукописные у Andys) показываются как есть.
 *
 * Переводим только там, где провайдер явно включил `translateInventory` —
 * это делает одна витрина (`App.jsx`). Провайдер есть и в дилерском бандле
 * (`/showroom`, `/cars/:id/landing`, оверлеи), и там, как и без провайдера,
 * `useTranslateVehicleValue` отдаёт прежнее поведение (fuel/transmission —
 * `translateValue`, остальное как есть): у дилеров не появляется «Essence».
 */
import { useCallback } from 'react';
import en from '../i18n/en.json';
import fr from '../i18n/fr.json';
import de from '../i18n/de.json';
import lu from '../i18n/lu.json';
import ru from '../i18n/ru.json';
import el from '../i18n/el.json';
import { translateValue } from '../components/print/translations';
import { useTranslation } from '../i18n/I18nContext';

const LOCALES = { en, fr, de, lu, ru, el };

/** Поля, для которых до #1417 работал `translateValue` — сохраняем его как fallback. */
const LEGACY_FIELDS = new Set(['fuelType', 'transmission']);

/** "Plug-in Hybrid" → "plug-in-hybrid", "360 Camera" → "360-camera". */
export function vehicleValueSlug(value) {
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function lookup(lang, field, slug) {
  const hit = LOCALES[lang]?.showcase?.values?.[field]?.[slug];
  return typeof hit === 'string' ? hit : undefined;
}

/**
 * @param {string|null|undefined} value — сырое значение из инвентаря
 * @param {'bodyStyle'|'fuelType'|'transmission'|'features'} field
 * @param {string} language — текущий язык витрины
 */
export function translateVehicleValue(value, field, language) {
  if (value == null || value === '') return value;
  const slug = vehicleValueSlug(value);
  const localized = lookup(language, field, slug) ?? lookup('en', field, slug);
  if (localized !== undefined) return localized;
  if (LEGACY_FIELDS.has(field)) return translateValue(value, language);
  return value;
}

/** Хук для компонентов: `(value, field) => label`; переводит только витрина. */
export function useTranslateVehicleValue() {
  const { language, translateInventory } = useTranslation();
  return useCallback(
    (value, field) => {
      if (translateInventory) return translateVehicleValue(value, field, language);
      if (LEGACY_FIELDS.has(field)) return translateValue(value, language);
      return value;
    },
    [language, translateInventory]
  );
}
